'use client';

import { createPortal } from 'react-dom';
import { useEffect, useState, useCallback, type FC, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { AnimatePresence, motion, type TargetAndTransition } from 'framer-motion';

import { cn } from '@/lib/utils';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';

type ModalAnimation = {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  exit: TargetAndTransition;
};

type AnimationStyle =
  | 'from-bottom'
  | 'from-center'
  | 'from-top'
  | 'from-left'
  | 'from-right'
  | 'fade'
  | 'top-in-bottom-out'
  | 'left-in-right-out';

interface MotionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  animationStyle?: AnimationStyle;
  closeOnOverlay?: boolean;
  showCloseButton?: boolean;
  viewTransitionName?: string;
}

const animationVariants: Record<AnimationStyle, ModalAnimation> = {
  'from-bottom': {
    initial: { y: '100%', opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: '100%', opacity: 0 },
  },
  'from-center': {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.95, opacity: 0 },
  },
  'from-top': {
    initial: { y: '-100%', opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: '-100%', opacity: 0 },
  },
  'from-left': {
    initial: { x: '-100%', opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '-100%', opacity: 0 },
  },
  'from-right': {
    initial: { x: '100%', opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '100%', opacity: 0 },
  },
  'fade': {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  'top-in-bottom-out': {
    initial: { y: '-100%', opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: '100%', opacity: 0 },
  },
  'left-in-right-out': {
    initial: { x: '-100%', opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '100%', opacity: 0 },
  },
};

const MotionModal: FC<MotionModalProps> = ({
  open,
  onOpenChange,
  children,
  className,
  contentClassName,
  animationStyle = 'from-center',
  closeOnOverlay = true,
  showCloseButton = true,
  viewTransitionName,
}) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useBodyScrollLock(open);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (!open) return;

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onKeyDown]);

  if (!mounted) return null;

  const selected = animationVariants[animationStyle];
  const close = () => onOpenChange(false);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md',
            className,
          )}
          onClick={() => closeOnOverlay && close()}
        >
          {showCloseButton && (
            <motion.button
              className="absolute top-4 right-4 text-white text-xl bg-neutral-900/50 ring-1 ring-border/50 backdrop-blur-md rounded-full p-2 dark:bg-neutral-100/50 dark:text-black cursor-pointer z-[99]"
              onClick={close}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Cerrar"
            >
              <X className="size-5" />
            </motion.button>
          )}
          <motion.div
            {...selected}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={cn(
              'relative w-full max-w-3xl mx-4 max-h-[85vh] overflow-y-auto',
              contentClassName,
            )}
            style={viewTransitionName ? { viewTransitionName } : undefined}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default MotionModal;
